import {
  clearOnboardingLiveSession,
  finishOnboarding,
  getOnboardingIsNew,
  getOnboardingState,
  isOnboardingLiveSession,
  markOnboardingLiveSession,
  patchOnboardingState,
  type OnboardingState,
} from "./selfSetupStorage";

export type SelfSetupTourStep = "dashboard" | "esgReports";

/** Steps shown in the welcome hub. */
export const SELF_SETUP_TOUR_ORDER: SelfSetupTourStep[] = [
  "dashboard",
  "esgReports",
];

export function getSelfSetupTourOrder(): SelfSetupTourStep[] {
  return SELF_SETUP_TOUR_ORDER;
}

/** Sub-phases within an active tour step (stored as spot for resume). */
export type SelfSetupTourPhase =
  | "nav"
  | "monthlyTab"
  | "month"
  | "customize"
  | "customizeSearch"
  | "customizeMetrics"
  | "customizeSave"
  | "kpi"
  | "kpiForm"
  | "kpiList"
  | "esgCards"
  | "charts"
  | "branchesTab"
  | "branches"
  | "compareBranches"
  | "compareDialog"
  | "compareResults"
  | "carbonTab"
  | "carbonDate"
  | "carbonContent"
  | "period"
  | "download"
  | "metricDefsTab"
  | "metricSearch"
  | "metricCards";

const DASHBOARD_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "monthlyTab",
  "month",
  "customize",
  "customizeSearch",
  "customizeMetrics",
  "customizeSave",
  "kpi",
  "kpiForm",
  "kpiList",
  "esgCards",
  "charts",
  "branchesTab",
  "branches",
  "compareBranches",
  "compareDialog",
  "compareResults",
  "carbonTab",
  "carbonDate",
  "carbonContent",
];

const ESG_REPORTS_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "period",
  "download",
  "metricDefsTab",
  "metricSearch",
  "metricCards",
];

function phasesForStep(step: SelfSetupTourStep): SelfSetupTourPhase[] {
  if (step === "dashboard") return DASHBOARD_TOUR_PHASES;
  return ESG_REPORTS_TOUR_PHASES;
}

function normalizeStep(
  raw: string | null | undefined,
): SelfSetupTourStep | null {
  if (!raw || raw === "dataSource") return null;
  return (SELF_SETUP_TOUR_ORDER as string[]).includes(raw)
    ? (raw as SelfSetupTourStep)
    : null;
}

function normalizePhase(
  step: SelfSetupTourStep,
  raw: string | null | undefined,
): SelfSetupTourPhase {
  // Legacy spots from earlier tour orders.
  if (step === "dashboard") {
    if (raw === "tabs") return "monthlyTab";
    if (raw === "kpiLimits") return "kpi";
  }
  if (step === "esgReports" && raw === "tabs") return "period";
  const allowed = phasesForStep(step);
  if (raw && (allowed as string[]).includes(raw)) {
    return raw as SelfSetupTourPhase;
  }
  return "nav";
}

function filterCompleted(completed: string[]): SelfSetupTourStep[] {
  const normalized = completed
    .map((s) => normalizeStep(s))
    .filter((s): s is SelfSetupTourStep => s != null);
  return SELF_SETUP_TOUR_ORDER.filter((s) => normalized.includes(s));
}

function readState(userId: string, organizationId: string): OnboardingState {
  return getOnboardingState(userId, organizationId);
}

/** Mark onboarding finished (congrats dismissed). */
export function finishSelfSetupTour(
  userId: string,
  organizationId: string,
): void {
  finishOnboarding(userId, organizationId);
}

export function getActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
): SelfSetupTourStep | null {
  const { page, spot } = readState(userId, organizationId);
  if (!page || spot === "hub" || spot === "congrats") return null;
  return normalizeStep(page);
}

export function setActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
): void {
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: "nav",
    resumePage: step,
    resumeSpot: "nav",
  });
  markOnboardingLiveSession();
}

/**
 * Pause mid-tour (Later): park on hub so welcome shows again, but keep the
 * exact tip in resumePage/resumeSpot for when the user presses Continue.
 */
export function pauseOnboardingAtTip(
  userId: string,
  organizationId: string,
  page: SelfSetupTourStep,
  spot: SelfSetupTourPhase,
): void {
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: page,
    resumeSpot: spot,
  });
  clearOnboardingLiveSession();
}

export function getSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep | null = getActiveSelfSetupTourStep(
    userId,
    organizationId,
  ),
): SelfSetupTourPhase {
  if (!step) return "nav";
  const { spot } = readState(userId, organizationId);
  if (spot === "hub" || spot === "congrats") return "nav";
  return normalizePhase(step, spot);
}

export function setSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  phase: SelfSetupTourPhase,
): void {
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return;
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: phase,
    resumePage: step,
    resumeSpot: phase,
  });
  markOnboardingLiveSession();
}

export function getCompletedSelfSetupSteps(
  userId: string,
  organizationId: string,
): SelfSetupTourStep[] {
  const { completed } = readState(userId, organizationId);
  return filterCompleted(completed);
}

export function markSelfSetupStepCompleted(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
): SelfSetupTourStep[] {
  const current = getCompletedSelfSetupSteps(userId, organizationId);
  if (current.includes(step)) return current;
  const next = [...current, step];
  const stillIncomplete = getFirstIncompleteSelfSetupStep(next);
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: stillIncomplete ? "hub" : "congrats",
    completed: next,
    resumePage: null,
    resumeSpot: undefined,
  });
  clearOnboardingLiveSession();
  return getCompletedSelfSetupSteps(userId, organizationId);
}

export function getFirstIncompleteSelfSetupStep(
  completed: SelfSetupTourStep[],
): SelfSetupTourStep | null {
  return SELF_SETUP_TOUR_ORDER.find((s) => !completed.includes(s)) ?? null;
}

export function getSelfSetupTourPath(
  step: SelfSetupTourStep,
  _organizationId: string,
): { path: string; state?: Record<string, unknown> } {
  switch (step) {
    case "dashboard":
      return { path: "/", state: { selfSetupTour: "dashboard" } };
    case "esgReports":
      return { path: "/esg-reports", state: { selfSetupTour: "esgReports" } };
  }
}

/** Save hub pause (Later from welcome) while keeping completed progress. */
export function pauseOnboardingAtHub(
  userId: string,
  organizationId: string,
): void {
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: null,
    resumeSpot: undefined,
  });
  clearOnboardingLiveSession();
}

/**
 * On logout / hard leave: if a tip is live, park it as resume so welcome
 * returns and Continue restores the exact spot.
 */
export function parkActiveOnboardingOnLogout(
  userId: string,
  organizationId: string,
): void {
  if (!userId || !organizationId) return;
  if (!getOnboardingIsNew(userId, organizationId)) return;
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return;
  const spot = getSelfSetupTourPhase(userId, organizationId, step);
  pauseOnboardingAtTip(userId, organizationId, step, spot);
}

/**
 * Park every live tip for a user (all orgs). Used on logout when org id may
 * already be cleared from the store / session.
 */
export function parkAllActiveOnboardingForUser(userId: string): void {
  if (!userId || typeof localStorage === "undefined") return;
  const prefix = "esg.onboarding_state.";
  const userPrefix = `${prefix}${userId}.`;
  const orgIds: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(userPrefix)) continue;
    const orgId = key.slice(userPrefix.length);
    if (orgId) orgIds.push(orgId);
  }
  for (const orgId of orgIds) {
    parkActiveOnboardingOnLogout(userId, orgId);
  }
}

/**
 * Park every live tip in localStorage (any user/org). Safety net on logout when
 * identity strings may not match the key that holds the live tip.
 */
export function parkAllLiveOnboardingTips(): void {
  if (typeof localStorage === "undefined") return;
  const prefix = "esg.onboarding_state.";
  const pairs: { userId: string; orgId: string }[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    const lastDot = rest.lastIndexOf(".");
    if (lastDot <= 0) continue;
    const userId = rest.slice(0, lastDot);
    const orgId = rest.slice(lastDot + 1);
    if (userId && orgId) pairs.push({ userId, orgId });
  }
  for (const { userId, orgId } of pairs) {
    parkActiveOnboardingOnLogout(userId, orgId);
  }
  clearOnboardingLiveSession();
}

/**
 * If localStorage still has a live tip but this tab has no live-session flag
 * (logout missed park, or new tab after session ended), park it as resume so
 * the welcome hub can show again.
 */
export function recoverOrphanLiveOnboardingTip(
  userId: string,
  organizationId: string,
): boolean {
  if (!userId || !organizationId) return false;
  if (isOnboardingLiveSession()) return false;
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return false;
  parkActiveOnboardingOnLogout(userId, organizationId);
  return true;
}

/**
 * Resolve where the user left off for Continue: resumePage+resumeSpot first,
 * else a live page+spot tip (not hub/congrats).
 */
export function getOnboardingResumePoint(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const state = readState(userId, organizationId);

  const resumePage = normalizeStep(state.resumePage ?? null);
  if (resumePage) {
    return {
      page: resumePage,
      spot: normalizePhase(resumePage, state.resumeSpot),
    };
  }

  const livePage = normalizeStep(state.page);
  if (livePage && state.spot !== "hub" && state.spot !== "congrats") {
    return {
      page: livePage,
      spot: normalizePhase(livePage, state.spot),
    };
  }

  return null;
}

/** Activate a saved resume point as the live tip (page + exact spot). */
export function activateOnboardingResume(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const resume = getOnboardingResumePoint(userId, organizationId);
  if (!resume) return null;
  patchOnboardingState(userId, organizationId, {
    page: resume.page,
    spot: resume.spot,
    resumePage: resume.page,
    resumeSpot: resume.spot,
  });
  markOnboardingLiveSession();
  return resume;
}

/** Whether stored state is waiting on the congrats dialog. */
export function isOnboardingCongratsPending(
  userId: string,
  organizationId: string,
): boolean {
  return readState(userId, organizationId).spot === "congrats";
}
